import { useParams, Link } from 'react-router-dom';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHelmet from '@/components/base/SEOHelmet';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const related = getRelatedComics(id || '', 4);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-24 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">你查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 55漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate || '2026-01-01',
    dateModified: comic.updateDate,
    image: comic.coverUrl,
    publisher: {
      '@type': 'Organization',
      name: '55漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://junyitea.cn/comic/${comic.id}`,
    },
  };

  return (
    <>
      <SEOHelmet
        title={`${comic.title} - ${comic.author} - 免费阅读 - 55漫画网`}
        description={`${comic.title}漫画在线阅读，作者${comic.author}。${comic.description.slice(0, 100)}... 在55漫画网免费阅读全集。`}
        keywords={`${comic.title}, ${comic.author}, ${comic.category}, 漫画免费阅读, 55漫画网`}
        canonicalUrl={`http://junyitea.cn/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverUrl}
        jsonLd={articleJsonLd}
      />

      <Navbar />

      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: comic.category, to: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-4">
              <div className="md:col-span-4 lg:col-span-3">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.coverUrl}
                    alt={comic.title}
                    title={`${comic.title} - ${comic.author}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="md:col-span-8 lg:col-span-9">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">{comic.category}</span>
                  <span className="text-foreground-400">|</span>
                  <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-primary-500' : 'text-accent-500'}`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-5">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-yellow-500 text-lg"></i>
                    <span className="text-xl font-heading font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">{comic.views}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-play-circle-line text-base"></i>
                    开始阅读
                  </Link>
                  <button className="px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-bookmark-line text-base"></i>
                    加入书架
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {comic.chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer border border-background-200/70"
                >
                  <span className="text-sm font-medium text-foreground-800 truncate">{ch.title}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">{ch.updateDate}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="md:col-span-3">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-2 h-2 rounded-full bg-foreground-900"></span>
                  <h2 className="text-lg font-heading font-bold text-foreground-950">读者评价</h2>
                </div>

                <div className="space-y-5">
                  {[
                    {
                      name: '漫画迷小王',
                      rating: 5,
                      text: '« 这部漫画的画风实在太精美了！每一帧都可以当作壁纸，故事情节也扣人心弦，完全被吸引住了。强烈推荐给喜欢恋爱题材的朋友们！ »',
                      platform: '55漫画网认证读者',
                    },
                    {
                      name: '阅读达人Lucy',
                      rating: 5,
                      text: '« 追了半年多了，每次更新都第一时间来看。作者的叙事能力太强了，人物塑造非常立体，感情线发展也很自然。年度最佳韩漫之一！ »',
                      platform: 'VIP读者',
                    },
                    {
                      name: '深夜漫迷',
                      rating: 4,
                      text: '« 整体非常不错，画风和剧情都在线。唯一的小遗憾是更新速度稍微慢了一些，但每话质量确实很高，值得等待。 »',
                      platform: '55漫画网读者',
                    },
                  ].map((review, i) => (
                    <div key={i} className="p-5 rounded-lg bg-background-100">
                      <div className="flex items-center gap-2 mb-2">
                        <strong className="text-sm text-foreground-900">{review.name}</strong>
                        <div className="flex items-center">
                          {Array.from({ length: review.rating }).map((_, j) => (
                            <i key={j} className="ri-star-fill text-green-500 text-xs"></i>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed italic">{review.text}</p>
                      <span className="text-xs text-foreground-400 mt-2 block">{review.platform}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20style%20portrait%20of%20a%20character%20looking%20upward%20with%20hope%2C%20black%20and%20white%20toned%20with%20subtle%20warm%20highlights%2C%20soft%20lighting%2C%20emotional%20expression%2C%20clean%20white%20background%2C%20artistic%20portrait%20photography%20style%2C%20cinematic%20quality&width=500&height=600&seq=comic-review-001&orientation=portrait"
                    alt="读者社区"
                    title="55漫画网读者社区"
                    className="w-full h-auto rounded-lg"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {related.map((r) => (
                  <ComicCard key={r.id} comic={r} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}